import React, { useMemo } from "react";
import { colors2, UI_CAPTION_SEMIBOLD, UI_TAG, TagLinkV2, TooltipV2 } from "oolib";
import { getRoutePathToPublishedContent } from "@okf/ootils";
import { getProperTitleFromContentTypeId } from "../../../utils/getProperTitleFromContentTypeId";
import { useGroupTagsBySubTheme } from "@/components/inputs/TextEditor/LexicalTextEditor/plugins/AnnoPlugin/AnnoLightbox/utils/useGroupTagsBySubTheme";
import mixpanel from "mixpanel-browser";

const TagChip = ({ dd, collectionId, contentTypeDisplay, contentTitle, SELF_MANAGED_BASE_CONFIGS }) => (
  <div
    style={{ maxWidth: '100%' }}
    onClick={(e) => {
      e.stopPropagation();
      e.preventDefault();
    }}
  >
    <div className="tagwrapper" onClick={() => {
      mixpanel.track("Anno Card - Tag Clicked", {
        Feature: 'Annotation Explorer',
        "Tag Name": dd.display,
        "Tag Category Name": getProperTitleFromContentTypeId(collectionId),
        "Content Type": contentTypeDisplay,
        "Content Title": contentTitle,
      })
    }}>
      {/* wrapperStyle.maxWidth so the tooltip's inline-block ref element
          respects our cap — otherwise the Tag's own max-width:100% has nothing
          to clamp to and the chip never ellipsizes. */}
      <TooltipV2 text={dd.display} wrapperStyle={{ maxWidth: '100%' }}>
        <TagLinkV2
          target={"_blank"}
          size={'XS'}
          display={dd.display}
          to={getRoutePathToPublishedContent({
            contentType: collectionId,
            contentId: dd._id,
            SELF_MANAGED_BASE_CONFIGS,
          })}
        />
      </TooltipV2>
    </div>
  </div>
);

const TagChipList = ({ children }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
    {children}
  </div>
);

export const TagList = ({ tags, title, contentTypeDisplay, contentTitle, SELF_MANAGED_BASE_CONFIGS, shouldGroupBySubTheme }) => {

  // Find the "tags" entry to group by subTheme
  const tagsEntry = tags && Object.values(tags).find(d => d?.collectionId === "tags");
  const groupedBySubTheme = useGroupTagsBySubTheme(
    tagsEntry?.data,
    shouldGroupBySubTheme
  );

  return (
    <div>
      <UI_TAG style={{ color: colors2.grey40 }}>{title}</UI_TAG>
      <div style={{ display: "flex", flexDirection: 'column', gap: '1.2rem', marginTop: '0.8rem' }}>
      {tags && Object.values(tags).map((d, index) => {
        if (!d?.data?.length) return null;

        // When grouping by subTheme, skip the standalone subThemes entry
        // since subTheme names are already shown as group labels
        if (shouldGroupBySubTheme && d.collectionId === "subThemes" && groupedBySubTheme) return null;

        // For "tags" collectionId when grouping is active: render grouped by subTheme
        if (shouldGroupBySubTheme && d.collectionId === "tags" && groupedBySubTheme) {
          return groupedBySubTheme.map((group) => (
            <div key={group.subThemeDisplay}>
              <UI_CAPTION_SEMIBOLD
                style={{ paddingBottom: "0.2rem", color: colors2.grey50 }}
              >
                {group.subThemeDisplay}
              </UI_CAPTION_SEMIBOLD>
              <TagChipList>
                {group.tags.map((dd, idx) => (
                  <TagChip
                    key={`${dd.display}-${idx}`}
                    dd={dd}
                    collectionId="tags"
                    contentTypeDisplay={contentTypeDisplay}
                    contentTitle={contentTitle}
                    SELF_MANAGED_BASE_CONFIGS={SELF_MANAGED_BASE_CONFIGS}
                  />
                ))}
              </TagChipList>
            </div>
          ));
        }

        return (
          <div key={`${d.collectionId}-${index}`}>
            <UI_CAPTION_SEMIBOLD
              style={{
                paddingBottom: "0.2rem",
                color: colors2.grey50
              }}
            >
              {getProperTitleFromContentTypeId(d.collectionId)}
            </UI_CAPTION_SEMIBOLD>
            <TagChipList>
              {d.data.map((dd, idx) => (
                <TagChip
                  key={`${dd.display}-${idx}`}
                  dd={dd}
                  collectionId={d.collectionId}
                  contentTypeDisplay={contentTypeDisplay}
                  contentTitle={contentTitle}
                  SELF_MANAGED_BASE_CONFIGS={SELF_MANAGED_BASE_CONFIGS}
                />
              ))}
            </TagChipList>
          </div>
        );
      })}
      </div>
    </div>
  );
};
